import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, CheckCircle2, Loader2, Circle, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/lib/toastBanner";
import { logEvent } from "@/lib/events";
import { sanitizeHtml } from "@/utils/sanitize";
import LexpErrorBoundary from "@/components/LexpErrorBoundary";
import FitToPanel from "@/components/FitToPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function LikertItem({ item, onSubmit }) {
  // Local component so a LEXP that contains a `likert` item does not crash
  // this page with a ReferenceError (which used to leave the learner on a
  // blank screen). Mirrors the LikertItem in LearnerStandaloneLexp.js.
  const [selected, setSelected] = useState(
    item.response?.likert_value != null
      ? Number(item.response.likert_value)
      : item.response?.selected_option_ids?.[0] != null
      ? Number(item.response.selected_option_ids[0])
      : null
  );
  const [submitted, setSubmitted] = useState(!!item.response);
  const [submitting, setSubmitting] = useState(false);
  const min = item.likert_scale_min ?? 0;
  const max = item.likert_scale_max ?? 5;
  const range = [];
  for (let i = min; i <= max; i++) range.push(i);
  const handleSubmit = async (val) => {
    setSelected(val);
    setSubmitting(true);
    try {
      await onSubmit({ likert_value: val, selected_option_ids: [String(val)] });
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save your answer. Please try again.");
    } finally { setSubmitting(false); }
  };
  return (
    <div className="space-y-3" data-testid={`likert-item-${item.id}`}>
      <div className="text-sm text-gray-700 font-body ql-rendered" dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.stem) }} />
      <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${range.length}, minmax(0, 1fr))` }}>
        {range.map((val) => (
          <button key={val} type="button" disabled={submitted || submitting}
            onClick={() => !submitted && handleSubmit(val)}
            data-testid={`likert-option-${val}`}
            className={`flex flex-col items-center justify-start gap-2 px-2 py-3 rounded-lg border font-body transition-all min-h-[120px] ${
              selected === val ? "border-blue-400 bg-blue-50 text-blue-800" : "border-gray-200 hover:border-gray-300 text-gray-700"
            } ${submitted ? "opacity-70" : ""}`}
          >
            <span className="text-2xl font-bold leading-none">{val}</span>
            {item.likert_labels?.[String(val)] && (
              <span className="text-sm text-gray-700 text-center leading-tight whitespace-normal break-words">{item.likert_labels[String(val)]}</span>
            )}
          </button>
        ))}
      </div>
      {submitted && <p className="text-xs text-green-600 font-body flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Response recorded</p>}
    </div>
  );
}

function McItem({ item, onSubmit }) {
  const [selected, setSelected] = useState(item.response?.selected_option_ids?.[0] || null);
  const [submitted, setSubmitted] = useState(!!item.response);
  const [feedback, setFeedback] = useState(item.response ? { correct: item.response.correct, correct_option_ids: [], earned_points: item.response.earned_points } : null);
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    if (item.response) {
      setSubmitted(true);
      setFeedback((prev) => ({
        correct: item.response.correct,
        earned_points: item.response.earned_points,
        correct_option_ids: prev?.correct_option_ids || [],
      }));
      if (item.response.selected_option_ids?.[0]) {
        setSelected(item.response.selected_option_ids[0]);
      }
    }
  }, [item.response]);

  const handleSubmit = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      const result = await onSubmit({ selected_option_ids: [selected] });
      setFeedback(result);
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save your answer. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3" data-testid={`mc-item-${item.id}`}>
      <div className="text-sm text-gray-700 font-body ql-rendered" dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.stem) }} />
      <div className="space-y-2">
        {(item.answer_options || []).map((opt) => {
          const isSelected = selected === opt.option_id;
          const isCorrect = feedback?.correct_option_ids?.includes(opt.option_id);
          let borderClass = "border-gray-200";
          if (submitted && feedback) {
            if (isCorrect) borderClass = "border-emerald-400 bg-emerald-50";
            else if (isSelected && !feedback.correct) borderClass = "border-red-400 bg-red-50";
          } else if (isSelected) {
            borderClass = "border-gray-400 bg-gray-50";
          }
          return (
            <button
              key={opt.option_id}
              type="button"
              disabled={submitted}
              onClick={() => setSelected(opt.option_id)}
              data-testid={`mc-option-${opt.option_id}`}
              className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all text-sm font-body flex items-center gap-3 ${borderClass} ${submitted ? "cursor-default" : "hover:border-gray-300 cursor-pointer"}`}
            >
              <div className={`h-4 w-4 rounded-full border-2 shrink-0 flex items-center justify-center ${isSelected ? "border-gray-500" : "border-gray-300"}`}>
                {isSelected && <div className="h-2 w-2 rounded-full bg-gray-500" />}
              </div>
              <span className="flex-1 text-gray-900">{opt.text}</span>
              {submitted && isCorrect && <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />}
              {submitted && isSelected && !isCorrect && !feedback?.correct && <XCircle className="h-4 w-4 text-red-500 shrink-0" />}
            </button>
          );
        })}
      </div>
      {!submitted && (
        <Button onClick={handleSubmit} disabled={!selected || submitting} data-testid={`mc-submit-${item.id}`}
          className="h-9 px-4 font-body text-sm text-white rounded-lg" style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}>
          {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}Submit Answer
        </Button>
      )}
      {submitted && feedback && (
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-body ${
          item.item_category === "survey" ? "bg-blue-50 text-blue-700" : feedback.correct ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
        }`} data-testid={`mc-feedback-${item.id}`}>
          {item.item_category === "survey" ? <CheckCircle2 className="h-4 w-4" /> : feedback.correct ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
          {item.item_category === "survey" ? "Response recorded" : feedback.correct ? "Correct!" : "Incorrect"}
          {item.item_category !== "survey" && <span className="ml-auto text-xs">{item.response?.earned_points ?? feedback.earned_points ?? 0}/{item.point_value} pts</span>}
        </div>
      )}
    </div>
  );
}

function SataItem({ item, onSubmit }) {
  const [selected, setSelected] = useState(new Set(item.response?.selected_option_ids || []));
  const [submitted, setSubmitted] = useState(!!item.response);
  const [feedback, setFeedback] = useState(item.response ? { correct: item.response.correct, correct_option_ids: [], earned_points: item.response.earned_points } : null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (item.response) {
      setSubmitted(true);
      setFeedback((prev) => ({
        correct: item.response.correct,
        earned_points: item.response.earned_points,
        correct_option_ids: prev?.correct_option_ids || [],
      }));
      if (item.response.selected_option_ids?.length) {
        setSelected(new Set(item.response.selected_option_ids));
      }
    }
  }, [item.response]);

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const handleSubmit = async () => {
    if (selected.size === 0) return;
    setSubmitting(true);
    try {
      const result = await onSubmit({ selected_option_ids: [...selected] });
      setFeedback(result);
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save your answer. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3" data-testid={`sata-item-${item.id}`}>
      <div className="text-sm text-gray-700 font-body ql-rendered" dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.stem) }} />
      <p className="text-xs text-gray-400 font-body">Select all that apply.</p>
      <div className="space-y-2">
        {(item.answer_options || []).map((opt) => {
          const isSelected = selected.has(opt.option_id);
          const isCorrect = feedback?.correct_option_ids?.includes(opt.option_id);
          let borderClass = "border-gray-200";
          if (submitted && feedback) {
            if (isCorrect && isSelected) borderClass = "border-emerald-400 bg-emerald-50";
            else if (isCorrect && !isSelected) borderClass = "border-amber-400 bg-amber-50";
            else if (isSelected && !isCorrect) borderClass = "border-red-400 bg-red-50";
          } else if (isSelected) {
            borderClass = "border-gray-400 bg-gray-50";
          }
          return (
            <button
              key={opt.option_id}
              type="button"
              disabled={submitted}
              onClick={() => toggle(opt.option_id)}
              data-testid={`sata-option-${opt.option_id}`}
              className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all text-sm font-body flex items-center gap-3 ${borderClass} ${submitted ? "cursor-default" : "hover:border-gray-300 cursor-pointer"}`}
            >
              <div className={`h-4 w-4 rounded-md border-2 shrink-0 flex items-center justify-center ${isSelected ? "border-gray-500 bg-gray-500" : "border-gray-300"}`}>
                {isSelected && <svg viewBox="0 0 12 12" className="h-3 w-3 text-white"><path d="M3 6l2 2 4-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
              </div>
              <span className="flex-1 text-gray-900">{opt.text}</span>
            </button>
          );
        })}
      </div>
      {!submitted && (
        <Button onClick={handleSubmit} disabled={selected.size === 0 || submitting} data-testid={`sata-submit-${item.id}`}
          className="h-9 px-4 font-body text-sm text-white rounded-lg" style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}>
          {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}Submit Answer
        </Button>
      )}
      {submitted && feedback && (
        <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-body ${
          item.item_category === "survey" ? "bg-blue-50 text-blue-700" : feedback.correct ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
        }`} data-testid={`sata-feedback-${item.id}`}>
          {item.item_category === "survey" ? <CheckCircle2 className="h-4 w-4" /> : feedback.correct ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
          {item.item_category === "survey" ? "Response recorded" : feedback.correct ? "All correct!" : "Partially correct"}
          {item.item_category !== "survey" && <span className="ml-auto text-xs">{item.response?.earned_points ?? feedback.earned_points ?? 0}/{item.point_value} pts</span>}
        </div>
      )}
    </div>
  );
}

function TaskItem({ item, onSubmit }) {
  const [completed, setCompleted] = useState(item.response?.task_completed || false);
  const [submitted, setSubmitted] = useState(!!item.response);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (item.response?.task_completed) {
      setCompleted(true);
      setSubmitted(true);
    }
  }, [item.response]);

  const handleToggle = async () => {
    if (submitted) return;
    setSubmitting(true);
    try {
      await onSubmit({ task_completed: true });
      setCompleted(true);
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not mark task complete. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3" data-testid={`task-item-${item.id}`}>
      <div className="text-sm text-gray-700 font-body ql-rendered" dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.stem) }} />
      <button
        onClick={handleToggle}
        disabled={submitted || submitting}
        data-testid={`task-toggle-${item.id}`}
        className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 transition-all w-full text-left ${
          completed ? "border-emerald-400 bg-emerald-50" : "border-gray-200 hover:border-gray-300"
        } ${submitted ? "cursor-default" : "cursor-pointer"}`}
      >
        {completed ? (
          <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" />
        ) : submitting ? (
          <Loader2 className="h-5 w-5 text-gray-400 animate-spin shrink-0" />
        ) : (
          <Circle className="h-5 w-5 text-gray-300 shrink-0" />
        )}
        <span className={`text-sm font-body ${completed ? "text-emerald-700" : "text-gray-900"}`}>
          {completed ? "Task completed" : "Mark task as complete"}
        </span>
        {completed && item.item_category !== "survey" && <span className="ml-auto text-xs text-emerald-600 font-body">{item.point_value} pts</span>}
      </button>
      {item.scoring_source === "external" && (
        <p className="text-xs text-gray-400 font-body">Scoring is managed externally (AG import).</p>
      )}
    </div>
  );
}

export default function LearnerLessonPage() {
  const { trackId, lexpId } = useParams();
  const navigate = useNavigate();
  const [track, setTrack] = useState(null);
  const [lexp, setLexp] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(false);
  // (setSubmittingTrack removed — activity nav bar / submit-track no longer here)
  const [loadWarning, setLoadWarning] = useState(null);

  useEffect(() => {
    // Force-load strategy: fetch track + items INDEPENDENTLY so that a
    // single failing request never prevents the page from rendering. If the
    // LEXP isn't found in the track's list (legacy data, race with a re-
    // ordering, etc.) we fall back to loading it standalone via the
    // /learner/lexps/{id} endpoint instead of kicking the learner off the
    // page. This is the fix for the "LEXP won't load in prod" report.
    let cancelled = false;
    const fetchData = async () => {
      const [trackRes, itemsRes] = await Promise.allSettled([
        axios.get(`${API}/learner/tracks/${trackId}`, { withCredentials: true }),
        axios.get(`${API}/learner/lexps/${lexpId}/items`, { withCredentials: true }),
      ]);
      if (cancelled) return;

      let resolvedLexp = null;
      let warnings = [];

      if (trackRes.status === "fulfilled") {
        setTrack(trackRes.value.data);
        const found = (trackRes.value.data.lexps || []).find((l) => l.lexp_id === lexpId);
        if (found) resolvedLexp = found;
      } else {
        warnings.push("track");
      }

      // If we don't have the LEXP from the track, fetch it standalone so
      // the page can still render its content + items.
      if (!resolvedLexp) {
        try {
          const { data: standalone } = await axios.get(
            `${API}/learner/lexps/${lexpId}${trackId ? `?track_id=${trackId}` : ""}`,
            { withCredentials: true }
          );
          resolvedLexp = { ...standalone, lexp_id: lexpId };
        } catch {
          warnings.push("lexp");
        }
      }

      if (resolvedLexp) setLexp(resolvedLexp);

      if (itemsRes.status === "fulfilled") {
        setItems(Array.isArray(itemsRes.value.data) ? itemsRes.value.data : []);
      } else {
        setItems([]);
        warnings.push("items");
      }

      if (warnings.length && (resolvedLexp || warnings.length < 3)) {
        setLoadWarning(
          warnings.includes("lexp")
            ? "This lesson couldn't load fully. Some content may be missing."
            : "Some lesson data couldn't load. You can still work with what's shown."
        );
      }
      setLoading(false);
    };
    fetchData();
    logEvent("lesson_viewed", { track_id: trackId, lexp_id: lexpId });
    return () => { cancelled = true; };
  }, [trackId, lexpId, navigate]);

  const handleItemSubmit = async (itemId, body) => {
    try {
      const { data } = await axios.post(
        `${API}/learner/lexps/${lexpId}/items/${itemId}/submit`,
        body,
        { withCredentials: true }
      );
      // Update item response in local state
      setItems((prev) =>
        prev.map((i) =>
          i.id === itemId ? { ...i, response: { ...body, correct: data.correct, earned_points: data.earned_points } } : i
        )
      );
      logEvent("item_answered", { item_id: itemId, correct: data.correct });
      return data;
    } catch (err) {
      // Re-throw so the McItem/SataItem catch surfaces the error toast.
      throw err;
    }
  };

  const handleMarkComplete = async () => {
    setMarking(true);
    try {
      await axios.post(`${API}/learner/tracks/${trackId}/lexps/${lexpId}/complete`, {}, { withCredentials: true });
      logEvent("lesson_completed", { track_id: trackId, lexp_id: lexpId });
      setLexp((prev) => ({ ...prev, completed: true }));
      toast.success("Marked as complete!");
    } catch {
      toast.error("Failed to mark complete");
    } finally {
      setMarking(false);
    }
  };

  // Track submission is now handled from the Learner Track view (header
  // "Submit Track" button). This page no longer exposes a submit-track
  // affordance; only marking the current LEXP complete happens here.

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  // Force-load fallback: if we have zero data at all, render an actionable
  // empty state instead of a silent blank screen.
  if (!lexp) {
    return (
      <div className="max-w-2xl mx-auto my-10 rounded-xl border border-gray-200 bg-white p-6" data-testid="lesson-load-failed">
        <h2 className="font-heading font-bold text-lg text-gray-900">
          This lesson couldn't be loaded
        </h2>
        <p className="text-sm text-gray-600 font-body mt-1">
          We couldn't fetch this lesson right now. Please try again in a moment.
        </p>
        <div className="mt-4 flex gap-2">
          <Button
            variant="outline"
            onClick={() => window.location.reload()}
            data-testid="lesson-load-retry"
          >
            Retry
          </Button>
          <Button
            onClick={() => navigate(trackId ? `/portal/tracks/${trackId}` : "/portal/dashboard")}
            data-testid="lesson-load-back"
            style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
          >
            Back
          </Button>
        </div>
      </div>
    );
  }

  const totalPts = items.reduce((s, i) => s + (i.item_category !== "survey" ? (i.point_value || 0) : 0), 0);
  const earnedPts = items.reduce((s, i) => s + (i.item_category !== "survey" ? (i.response?.earned_points || 0) : 0), 0);

  return (
    <LexpErrorBoundary>
    <div
      className="flex flex-col gap-3 sm:gap-4 lg:h-[calc(100vh-9rem)] min-h-[520px]"
      data-testid="learner-lesson-page"
    >
      {loadWarning && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-body text-amber-800" data-testid="lesson-load-warning">
          {loadWarning}
        </div>
      )}
      {/* ── Top strip: back button + title + score summary. Compact so the
            two-panel main area gets the majority of the viewport. ── */}
      <div className="flex items-start justify-between gap-3 sm:gap-4 flex-shrink-0">
        <div className="min-w-0 flex-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/portal/tracks/${trackId}`)}
            className="text-gray-500 hover:text-gray-900 hover:bg-gray-100 w-fit -ml-1 mb-1"
            data-testid="lesson-back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            Back to {track?.title || "Track"}
          </Button>
          <h1
            className="font-heading text-xl sm:text-2xl font-bold text-gray-900 truncate"
            data-testid="lesson-title"
          >
            {lexp.title}
          </h1>
          {lexp.description && (
            <p className="text-gray-500 font-body mt-0.5 text-xs sm:text-sm line-clamp-2">
              {lexp.description}
            </p>
          )}
        </div>
        {items.length > 0 && (
          <div className="shrink-0 text-right">
            {lexp.is_preliminary && !lexp.is_finalized ? (
              <span
                className="text-xs sm:text-sm font-body text-gray-500"
                data-testid="lesson-score-pending"
              >
                Score pending review
              </span>
            ) : totalPts > 0 ? (
              <div className="inline-flex flex-col items-end px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200">
                <span className="text-[10px] font-body uppercase tracking-widest text-gray-400">
                  Score
                </span>
                <span className="text-sm font-body">
                  <span className="font-bold text-gray-900">{earnedPts}</span>
                  <span className="text-gray-400"> / {totalPts}</span>
                </span>
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* ── Two-panel main area. Stacks on small viewports and sits side-by-
            side from `lg:` so both panels are visible simultaneously without
            page scroll. Each panel scrolls internally. ── */}
      <div
        className="flex-1 min-h-0 flex flex-col lg:flex-row gap-3 sm:gap-4"
      >
        {/* Left panel — learning content (scenario, embed, tasks, photo). */}
        <div
          className="w-full lg:w-[60%] bg-white border border-gray-200 rounded-xl flex flex-col overflow-hidden lg:min-h-0"
          data-testid="lesson-left-panel"
        >
          <div className="px-4 sm:px-6 py-2.5 border-b border-gray-100 flex-shrink-0">
            <p className="text-[10px] font-body uppercase tracking-widest text-gray-400">
              Learning Content
            </p>
          </div>
          <div className="lg:flex-1 lg:min-h-0 px-4 sm:px-6 py-4 sm:py-5">
            <FitToPanel testId="lesson-left-fit">
            <div className="space-y-5">
            {lexp.photo_url && (
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}${lexp.photo_url}`}
                alt=""
                className="w-full h-40 sm:h-48 object-cover rounded-xl"
                data-testid="lesson-photo"
              />
            )}
            {lexp.patient_scenario && (
              <div data-testid="lesson-scenario">
                <div
                  className="text-sm text-gray-900 font-body prose prose-sm max-w-none ql-rendered"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(lexp.patient_scenario) }}
                />
              </div>
            )}
            {lexp.embed_enabled && lexp.embed_url && (
              <div className="space-y-2" data-testid="lesson-embed-section">
                {lexp.embed_title && (
                  <h2
                    className="font-body font-bold text-base text-gray-900"
                    data-testid="lesson-embed-title"
                  >
                    {lexp.embed_title}
                  </h2>
                )}
                <div className="rounded-lg overflow-hidden">
                  <iframe
                    src={lexp.embed_url}
                    title={lexp.embed_title || "Embedded content"}
                    className="w-full border-0"
                    style={{ height: "460px" }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    data-testid="lesson-embed-iframe"
                  />
                </div>
              </div>
            )}
            {lexp.tasks && (
              <div className="pt-1" data-testid="lesson-tasks">
                <h2 className="font-body font-bold text-base text-gray-900 mb-2">
                  Tasks
                </h2>
                <div
                  className="text-sm text-gray-600 font-body prose prose-sm max-w-none ql-rendered"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(lexp.tasks) }}
                />
              </div>
            )}
            {!lexp.patient_scenario &&
              !(lexp.embed_enabled && lexp.embed_url) &&
              !lexp.tasks &&
              !lexp.photo_url && (
                <p className="text-sm text-gray-400 font-body italic text-center py-8">
                  No learning content for this lesson.
                </p>
              )}
            </div>
            </FitToPanel>
          </div>
        </div>

        {/* Right panel — assessment items (questions + answers). */}
        <div
          className="w-full lg:w-[40%] bg-white border border-gray-200 rounded-xl flex flex-col overflow-hidden lg:min-h-0"
          data-testid="lesson-right-panel"
        >
          <div className="px-4 sm:px-6 py-2.5 border-b border-gray-100 flex-shrink-0 flex items-center justify-between gap-3">
            <p className="text-[10px] font-body uppercase tracking-widest text-gray-400">
              Assessment Items
            </p>
            {items.length > 0 && (
              <span className="text-[10px] font-body text-gray-400 tabular-nums">
                {items.length} {items.length === 1 ? "item" : "items"}
              </span>
            )}
          </div>
          <div
            className="lg:flex-1 lg:overflow-y-auto px-4 sm:px-6 py-4 sm:py-5"
            data-testid="lesson-items-section"
          >
            {items.length === 0 ? (
              <p className="text-sm text-gray-400 font-body italic text-center py-8">
                No assessment items in this lesson.
              </p>
            ) : (
              <div className="space-y-4">
                {items.map((item, idx) => (
                  <div
                    key={item.id}
                    className="border border-gray-200 rounded-xl p-4 sm:p-5 bg-white"
                    data-testid={`lesson-item-${item.id}`}
                  >
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      <span className="text-xs text-gray-400 font-body">
                        {idx + 1}.
                      </span>
                      <Badge
                        className={`text-xs font-body ${
                          item.item_type === "mc"
                            ? "bg-blue-50 text-blue-600 border-blue-200"
                            : item.item_type === "sata"
                            ? "bg-purple-50 text-purple-600 border-purple-200"
                            : item.item_type === "cfi_mc"
                            ? "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200"
                            : item.item_type === "likert"
                            ? "bg-teal-50 text-teal-700 border-teal-200"
                            : "bg-amber-50 text-amber-600 border-amber-200"
                        }`}
                      >
                        {item.item_type === "mc"
                          ? "Multiple Choice"
                          : item.item_type === "sata"
                          ? "Select All"
                          : item.item_type === "cfi_mc"
                          ? "CFI MC"
                          : item.item_type === "likert"
                          ? "Confidence"
                          : "Task"}
                      </Badge>
                      <span className="text-xs text-gray-400 font-body ml-auto">
                        {item.item_category !== "survey"
                          ? `${item.point_value} pt${item.point_value !== 1 ? "s" : ""}`
                          : "Survey"}
                      </span>
                    </div>
                    {(item.item_type === "mc" || item.item_type === "cfi_mc") && (
                      <McItem item={item} onSubmit={(body) => handleItemSubmit(item.id, body)} />
                    )}
                    {item.item_type === "sata" && (
                      <SataItem item={item} onSubmit={(body) => handleItemSubmit(item.id, body)} />
                    )}
                    {item.item_type === "task" && (
                      <TaskItem item={item} onSubmit={(body) => handleItemSubmit(item.id, body)} />
                    )}
                    {item.item_type === "likert" && (
                      <LikertItem item={item} onSubmit={(body) => handleItemSubmit(item.id, body)} />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Bottom bar: mark-complete + track navigation. Sticks at the
            bottom of the two-panel container so it's always visible. ── */}
      <div className="flex-shrink-0 flex flex-col sm:flex-row gap-2 sm:gap-3">
        <div
          className="flex-1 bg-white border border-gray-200 rounded-xl px-4 py-2.5 flex items-center justify-between gap-3 min-w-0"
          data-testid="lesson-completion-section"
        >
          {lexp.completed ? (
            <div className="flex items-center gap-2.5 min-w-0">
              <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" />
              <div className="min-w-0">
                <p className="font-body font-bold text-gray-900 text-sm leading-tight">
                  Completed
                </p>
                <p className="text-xs text-gray-400 font-body truncate">
                  {lexp.completed_at
                    ? `Completed on ${new Date(lexp.completed_at).toLocaleDateString()}`
                    : "Well done!"}
                </p>
              </div>
            </div>
          ) : (
            <>
              <div className="min-w-0">
                <p className="font-body font-bold text-gray-900 text-sm leading-tight">
                  Ready to finish?
                </p>
                <p className="text-xs text-gray-400 font-body truncate">
                  Mark this lesson as complete when you're done.
                </p>
              </div>
              <Button
                onClick={handleMarkComplete}
                disabled={marking}
                data-testid="mark-complete-btn"
                className="h-9 sm:h-10 px-4 font-body font-medium text-white rounded-xl hover:opacity-90 shrink-0"
                style={{
                  backgroundColor: "var(--primary-color, #4A90D9)",
                  color: "var(--on-secondary, #FFFFFF)",
                }}
              >
                {marking ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 mr-1.5" />
                )}
                Mark Complete
              </Button>
            </>
          )}
        </div>

        {/* Track-level activity navigation bar has been intentionally
            removed — learners should not be able to skip between LEXPs
            from within a LEXP. Only per-item nav (in the right panel)
            remains, plus the mark-complete action above which returns
            them to the track view. */}
      </div>
    </div>
    </LexpErrorBoundary>
  );
}
